import { createHash } from "node:crypto";
import {
  chmod,
  cp,
  mkdir,
  readdir,
  readFile,
  realpath,
  rm,
  stat,
  writeFile,
} from "node:fs/promises";
import { createRequire } from "node:module";
import { basename, dirname, join, relative } from "node:path";
import process from "node:process";
import { fileURLToPath } from "node:url";
import { readDaemonRuntimeLease } from "../../config/daemon-runtime-lease";
import { readDaemonServiceContract } from "../../config/daemon-service-contract";
import {
  daemonRuntimeBundlesPath,
  daemonRuntimeRootPath,
  daemonRuntimeServiceManagerPath,
  daemonRuntimeStateRootPath,
  daemonServiceActiveBundleStatePath,
  daemonServiceLegacyExecutableWrapperPath,
  daemonServiceRecognizedExecutableWrapperPaths,
  daemonServiceStableExecutableWrapperPath,
  daemonServiceStableLauncherPath,
  daemonStatePath,
  getDaemonStatus,
} from "../../config/daemon-state";
import {
  resolveWakeLoopEnvPreset,
  type WakeLoopPathResolutionInput,
} from "../../config/paths";
import { performManagedInstall } from "../../local-components/managed-install";
import { isBinaryDistribution } from "../../runtime/distribution";
import { resolveDaemonServiceArtifact } from "./daemon-service-artifact";

const DAEMON_ACTIVE_BUNDLE_SCHEMA = "wakeloop-daemon-active-bundle-v2";
const DAEMON_ACTIVE_BUNDLE_LEGACY_SCHEMA = "wakeloop-daemon-active-bundle-v1";
const DAEMON_CLI_ENTRY_NAMES = ["wakeloop-dev.js", "wakeloop.js"] as const;
const DAEMON_BUNDLE_SOURCE_KIND_WORKSPACE = "workspace";
const DAEMON_BUNDLE_SOURCE_KIND_PACKAGE_INSTALL = "package_install";
const DAEMON_BUNDLE_SOURCE_KIND_NPX_CACHE = "npx_cache";
const DAEMON_BUNDLE_SOURCE_KIND_UNKNOWN = "unknown";
const DAEMON_SOURCE_DIST_IGNORED_TOP_LEVEL_DIRS = new Set(["binary"]);

export type DaemonBundleFormat =
  | "legacy_linked_v1"
  | "self_contained_v1"
  | "binary_v1";
export type DaemonBundleSourceKind =
  | typeof DAEMON_BUNDLE_SOURCE_KIND_WORKSPACE
  | typeof DAEMON_BUNDLE_SOURCE_KIND_PACKAGE_INSTALL
  | typeof DAEMON_BUNDLE_SOURCE_KIND_NPX_CACHE
  | typeof DAEMON_BUNDLE_SOURCE_KIND_UNKNOWN;

export type DaemonActiveBundleState = {
  v: 2;
  schema: typeof DAEMON_ACTIVE_BUNDLE_SCHEMA;
  bundleId: string;
  daemonVersion: string;
  entryScriptRelativePath: string;
  bundleFormat: DaemonBundleFormat;
  sourceEntryScriptPath: string;
  sourcePackageRootPath: string;
  sourceDistRootPath: string;
  sourceDistFingerprint?: string | null;
  sourceKind: DaemonBundleSourceKind;
  createdAt: string;
  updatedAt: string;
};

export function daemonServiceActiveBundlePath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return daemonServiceActiveBundleStatePath(pathInput);
}

export function daemonServiceLauncherPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return daemonServiceStableLauncherPath(pathInput);
}

export function daemonServiceExecutableWrapperPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return daemonServiceStableExecutableWrapperPath(pathInput);
}

export function daemonServiceExecutableWrapperPaths(
  pathInput: WakeLoopPathResolutionInput = {}
): string[] {
  return daemonServiceRecognizedExecutableWrapperPaths(pathInput);
}

export function daemonServiceLegacyWrapperPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return daemonServiceLegacyExecutableWrapperPath(pathInput);
}

export function resolveDaemonBundleRootPath(
  bundleId: string,
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(daemonRuntimeBundlesPath(pathInput), bundleId);
}

export function isDaemonBundleSourceAllowedForLane(input: {
  sourceKind: DaemonBundleSourceKind;
  pathInput?: WakeLoopPathResolutionInput;
}): boolean {
  if (input.sourceKind !== DAEMON_BUNDLE_SOURCE_KIND_WORKSPACE) {
    return true;
  }
  return resolveWakeLoopEnvPreset(input.pathInput) === "dev-local";
}

export async function readDaemonActiveBundleState(): Promise<DaemonActiveBundleState | null> {
  const raw = await readFile(daemonServiceActiveBundlePath(), "utf8").catch(
    (error) => {
      if (getErrorCode(error) === "ENOENT") {
        return null;
      }
      throw error;
    }
  );
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<DaemonActiveBundleState>;
    if (parsed.v === 2 && parsed.schema === DAEMON_ACTIVE_BUNDLE_SCHEMA) {
      if (
        typeof parsed.bundleId !== "string" ||
        parsed.bundleId.length === 0 ||
        typeof parsed.daemonVersion !== "string" ||
        parsed.daemonVersion.length === 0 ||
        typeof parsed.entryScriptRelativePath !== "string" ||
        parsed.entryScriptRelativePath.length === 0 ||
        !isDaemonBundleFormat(parsed.bundleFormat) ||
        typeof parsed.sourceEntryScriptPath !== "string" ||
        parsed.sourceEntryScriptPath.length === 0 ||
        typeof parsed.sourcePackageRootPath !== "string" ||
        parsed.sourcePackageRootPath.length === 0 ||
        typeof parsed.sourceDistRootPath !== "string" ||
        parsed.sourceDistRootPath.length === 0 ||
        !isDaemonBundleSourceKind(parsed.sourceKind) ||
        typeof parsed.createdAt !== "string" ||
        parsed.createdAt.length === 0 ||
        typeof parsed.updatedAt !== "string" ||
        parsed.updatedAt.length === 0
      ) {
        return null;
      }
      return {
        v: 2,
        schema: DAEMON_ACTIVE_BUNDLE_SCHEMA,
        bundleId: parsed.bundleId,
        daemonVersion: parsed.daemonVersion,
        entryScriptRelativePath: parsed.entryScriptRelativePath,
        bundleFormat: parsed.bundleFormat,
        sourceEntryScriptPath: parsed.sourceEntryScriptPath,
        sourcePackageRootPath: parsed.sourcePackageRootPath,
        sourceDistRootPath: parsed.sourceDistRootPath,
        sourceDistFingerprint: normalizeOptionalText(
          parsed.sourceDistFingerprint
        ),
        sourceKind: parsed.sourceKind,
        createdAt: parsed.createdAt,
        updatedAt: parsed.updatedAt,
      };
    }

    const legacyParsed = parsed as Partial<{
      v: 1;
      schema: string;
      bundleId: string;
      daemonVersion: string;
      entryScriptRelativePath: string;
      sourceDistRootPath: string;
      createdAt: string;
      updatedAt: string;
    }>;
    if (
      legacyParsed.v !== 1 ||
      legacyParsed.schema !== DAEMON_ACTIVE_BUNDLE_LEGACY_SCHEMA ||
      typeof legacyParsed.bundleId !== "string" ||
      legacyParsed.bundleId.length === 0 ||
      typeof legacyParsed.daemonVersion !== "string" ||
      legacyParsed.daemonVersion.length === 0 ||
      typeof legacyParsed.entryScriptRelativePath !== "string" ||
      legacyParsed.entryScriptRelativePath.length === 0 ||
      typeof legacyParsed.sourceDistRootPath !== "string" ||
      legacyParsed.sourceDistRootPath.length === 0 ||
      typeof legacyParsed.createdAt !== "string" ||
      legacyParsed.createdAt.length === 0 ||
      typeof legacyParsed.updatedAt !== "string" ||
      legacyParsed.updatedAt.length === 0
    ) {
      return null;
    }
    const sourceEntryScriptPath = join(
      legacyParsed.sourceDistRootPath,
      basename(legacyParsed.entryScriptRelativePath)
    );
    const sourcePackageRootPath = dirname(legacyParsed.sourceDistRootPath);
    return {
      v: 2,
      schema: DAEMON_ACTIVE_BUNDLE_SCHEMA,
      bundleId: legacyParsed.bundleId,
      daemonVersion: legacyParsed.daemonVersion,
      entryScriptRelativePath: legacyParsed.entryScriptRelativePath,
      bundleFormat: "legacy_linked_v1",
      sourceEntryScriptPath,
      sourcePackageRootPath,
      sourceDistRootPath: legacyParsed.sourceDistRootPath,
      sourceDistFingerprint: null,
      sourceKind: inferDaemonBundleSourceKind(sourcePackageRootPath),
      createdAt: legacyParsed.createdAt,
      updatedAt: legacyParsed.updatedAt,
    };
  } catch {
    return null;
  }
}

export async function resolveAlignedDaemonActiveBundleEntryPath(
  input: { expectedVersion?: string } = {}
): Promise<string | null> {
  const [installStatus, serviceContract, state] = await Promise.all([
    getDaemonStatus(),
    readDaemonServiceContract().catch(() => null),
    readDaemonActiveBundleState(),
  ]);
  const expectedVersion =
    input.expectedVersion ??
    serviceContract?.daemonVersion ??
    (installStatus.installed
      ? installStatus.state.daemonVersion
      : (state?.daemonVersion ?? "unknown"));
  const matchingServiceContract =
    input.expectedVersion &&
    serviceContract?.daemonVersion !== input.expectedVersion
      ? null
      : serviceContract;
  const currentSourceDistFingerprint =
    await resolveCurrentDaemonFingerprintForState(state).catch(() => null);
  const activeBundleEntryPath =
    await resolveDaemonBundleEntryPathFromState(state);
  const activeBundleSourceAvailable =
    state && state.bundleFormat === "legacy_linked_v1"
      ? await pathExists(state.sourcePackageRootPath).catch(() => null)
      : null;
  const artifact = resolveDaemonServiceArtifact({
    expectedVersion,
    installStatus,
    serviceContract: matchingServiceContract,
    activeBundle: state,
    activeBundleSourceAvailable,
    activeBundleEntryPath,
    currentSourceDistFingerprint,
  });
  return artifact.resolvedEntryPath;
}

async function resolveCurrentDaemonFingerprintForState(
  state: DaemonActiveBundleState | null
): Promise<string | null> {
  if (state?.bundleFormat === "binary_v1") {
    return await hashDaemonBinaryFile(state.sourceEntryScriptPath);
  }
  return await resolveCurrentDaemonSourceDistFingerprint();
}

export async function resolveDaemonBundleEntryPathFromState(
  state: DaemonActiveBundleState | null,
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<string | null> {
  if (!state) {
    return null;
  }
  if (state.bundleFormat === "binary_v1") {
    return (await pathExists(state.sourceEntryScriptPath))
      ? state.sourceEntryScriptPath
      : null;
  }
  const entryPath = join(
    resolveDaemonBundleRootPath(state.bundleId, pathInput),
    state.entryScriptRelativePath
  );
  return (await pathExists(entryPath)) ? entryPath : null;
}

export async function prepareDaemonServiceBundle(input: {
  daemonVersion: string;
  cliScriptPath?: string;
}): Promise<DaemonActiveBundleState> {
  if (isBinaryDistribution()) {
    return await prepareBinaryDaemonServiceBundle();
  }
  const sourceProvenance = await resolveDaemonBundleSourceProvenance(
    input.cliScriptPath
  );
  if (
    !isDaemonBundleSourceAllowedForLane({
      sourceKind: sourceProvenance.sourceKind,
    })
  ) {
    throw new Error(
      "WakeLoop Service cannot use repo-local workspace service files in this lane. Repo-local service files are only allowed through `wakeloop-dev`."
    );
  }
  const { sourceEntryScriptPath, sourceDistRootPath, sourcePackageRootPath } =
    sourceProvenance;
  const sourceDistFingerprint =
    await computeDaemonSourceDistFingerprint(sourceDistRootPath);
  const entryScriptRelativePath = basename(sourceEntryScriptPath);
  const now = new Date().toISOString();
  const bundleId = createBundleId(input.daemonVersion);
  const bundleRootPath = resolveDaemonBundleRootPath(bundleId);
  const targetDistRootPath = join(bundleRootPath, "dist");

  await Promise.all([
    mkdir(daemonRuntimeRootPath(), { recursive: true }),
    mkdir(daemonRuntimeBundlesPath(), { recursive: true }),
    mkdir(daemonRuntimeStateRootPath(), { recursive: true }),
    mkdir(daemonRuntimeServiceManagerPath(), { recursive: true }),
  ]);

  await rm(bundleRootPath, { recursive: true, force: true });
  await mkdir(bundleRootPath, { recursive: true });
  await cp(sourceDistRootPath, targetDistRootPath, {
    recursive: true,
    force: true,
    filter: (sourcePath) =>
      isDaemonSourceDistPathIncluded(sourceDistRootPath, sourcePath),
  });
  await writeDaemonBundlePackageManifest({
    bundleRootPath,
    daemonVersion: input.daemonVersion,
  });
  await copyDaemonBundleRuntimeDependencies({
    bundleRootPath,
    sourcePackageRootPath,
  });

  const nextState: DaemonActiveBundleState = {
    v: 2,
    schema: DAEMON_ACTIVE_BUNDLE_SCHEMA,
    bundleId,
    daemonVersion: input.daemonVersion,
    entryScriptRelativePath: join("dist", entryScriptRelativePath),
    bundleFormat: "self_contained_v1",
    sourceEntryScriptPath,
    sourcePackageRootPath,
    sourceDistRootPath,
    sourceDistFingerprint,
    sourceKind: sourceProvenance.sourceKind,
    createdAt: now,
    updatedAt: now,
  };

  await writeFile(
    daemonServiceActiveBundlePath(),
    `${JSON.stringify(nextState, null, 2)}\n`,
    {
      encoding: "utf8",
      mode: 0o600,
    }
  );
  await ensureDaemonServiceManagerExecutables();

  return nextState;
}

// Binary distribution: the daemon runs this same binary via `service run`, so
// there is no dist copy or node_modules dependency closure. Anchor the daemon
// to the managed install (versions/<version>/wakeloop) and record a binary_v1
// active-bundle state (path fields kept non-empty for the read whitelist). No
// launcher/wrapper is generated; command-spec points straight at the binary.
async function prepareBinaryDaemonServiceBundle(): Promise<DaemonActiveBundleState> {
  const outcome = await performManagedInstall();
  const managedBinaryPath = outcome.managedBinaryPath;
  if (!(await pathExists(managedBinaryPath))) {
    throw new Error(
      `WakeLoop managed binary is missing at ${managedBinaryPath}; cannot prepare binary WakeLoop Service.`
    );
  }
  const managedVersionDir = dirname(managedBinaryPath);
  const now = new Date().toISOString();

  await Promise.all([
    mkdir(daemonRuntimeRootPath(), { recursive: true }),
    mkdir(daemonRuntimeStateRootPath(), { recursive: true }),
    mkdir(daemonRuntimeServiceManagerPath(), { recursive: true }),
  ]);

  const nextState: DaemonActiveBundleState = {
    v: 2,
    schema: DAEMON_ACTIVE_BUNDLE_SCHEMA,
    bundleId: createBundleId(outcome.version),
    daemonVersion: outcome.version,
    entryScriptRelativePath: "wakeloop",
    bundleFormat: "binary_v1",
    sourceEntryScriptPath: managedBinaryPath,
    sourcePackageRootPath: managedVersionDir,
    sourceDistRootPath: managedVersionDir,
    sourceDistFingerprint: await hashDaemonBinaryFile(managedBinaryPath).catch(
      () => null
    ),
    sourceKind: DAEMON_BUNDLE_SOURCE_KIND_PACKAGE_INSTALL,
    createdAt: now,
    updatedAt: now,
  };

  await writeFile(
    daemonServiceActiveBundlePath(),
    `${JSON.stringify(nextState, null, 2)}\n`,
    { encoding: "utf8", mode: 0o600 }
  );
  return nextState;
}

async function hashDaemonBinaryFile(filePath: string): Promise<string> {
  const hash = createHash("sha256");
  hash.update(await readFile(filePath));
  return hash.digest("hex");
}

async function writeDaemonBundlePackageManifest(input: {
  bundleRootPath: string;
  daemonVersion: string;
}): Promise<void> {
  const packageManifestPath = join(input.bundleRootPath, "package.json");
  await writeFile(
    packageManifestPath,
    `${JSON.stringify(
      {
        name: "wakeloop-daemon-service-bundle",
        private: true,
        type: "module",
        version: input.daemonVersion,
      },
      null,
      2
    )}\n`,
    {
      encoding: "utf8",
      mode: 0o600,
    }
  );
}

async function copyDaemonBundleRuntimeDependencies(input: {
  bundleRootPath: string;
  sourcePackageRootPath: string;
}): Promise<void> {
  const runtimeDependencies = await collectDaemonBundleRuntimeDependencyClosure(
    {
      sourcePackageRootPath: input.sourcePackageRootPath,
    }
  );

  const targetNodeModulesPath = join(input.bundleRootPath, "node_modules");
  await rm(targetNodeModulesPath, {
    recursive: true,
    force: true,
  });
  await mkdir(targetNodeModulesPath, { recursive: true });
  for (const runtimeDependency of runtimeDependencies) {
    await copyDaemonBundleDependencyDirectory({
      dependencyName: runtimeDependency.dependencyName,
      sourceDependencyPath: runtimeDependency.packageRootPath,
      targetNodeModulesPath,
    });
  }
}

async function copyDaemonBundleDependencyDirectory(input: {
  dependencyName: string;
  sourceDependencyPath: string;
  targetNodeModulesPath: string;
}): Promise<void> {
  if (!(await pathExists(input.sourceDependencyPath))) {
    throw new Error(
      `WakeLoop could not copy runtime dependency ${input.dependencyName} for background service bundling from ${input.sourceDependencyPath}. Run \`pnpm install\` and retry.`
    );
  }
  const targetDependencyPath = join(
    input.targetNodeModulesPath,
    ...input.dependencyName.split("/")
  );
  await mkdir(dirname(targetDependencyPath), { recursive: true });
  await cp(input.sourceDependencyPath, targetDependencyPath, {
    recursive: true,
    force: true,
    dereference: true,
    preserveTimestamps: true,
  });
}

export async function writeDaemonServiceLauncher(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  await mkdir(daemonRuntimeServiceManagerPath(pathInput), { recursive: true });
  await writeFile(
    daemonServiceLauncherPath(pathInput),
    `${buildDaemonServiceLauncherSource(pathInput)}\n`,
    {
      encoding: "utf8",
      mode: 0o700,
    }
  );
}

export async function writeDaemonServiceExecutableWrapper(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  await mkdir(daemonRuntimeServiceManagerPath(pathInput), { recursive: true });
  await Promise.all(
    daemonServiceExecutableWrapperPaths(pathInput).map(async (wrapperPath) => {
      await writeFile(
        wrapperPath,
        `${buildDaemonServiceExecutableWrapperSource(pathInput)}\n`,
        {
          encoding: "utf8",
          mode: 0o700,
        }
      );
      await chmod(wrapperPath, 0o700);
    })
  );
}

export async function ensureDaemonServiceManagerExecutables(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  await writeDaemonServiceLauncher(pathInput);
  await writeDaemonServiceExecutableWrapper(pathInput);
}

export async function restoreDaemonActiveBundleStateSnapshot(
  snapshot: DaemonActiveBundleState | null,
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  if (!snapshot) {
    await rm(daemonServiceActiveBundlePath(pathInput), { force: true });
    return;
  }
  await mkdir(daemonRuntimeStateRootPath(pathInput), { recursive: true });
  await writeFile(
    daemonServiceActiveBundlePath(pathInput),
    `${JSON.stringify(snapshot, null, 2)}\n`,
    {
      encoding: "utf8",
      mode: 0o600,
    }
  );
}

export async function clearDaemonServiceBundles(): Promise<void> {
  await Promise.all([
    rm(daemonServiceActiveBundlePath(), { force: true }),
    rm(daemonServiceLauncherPath(), { force: true }),
    ...daemonServiceExecutableWrapperPaths().map((wrapperPath) =>
      rm(wrapperPath, { force: true })
    ),
    rm(daemonRuntimeBundlesPath(), { recursive: true, force: true }),
  ]);
}

export async function cleanupInactiveDaemonServiceBundles(
  input: { retainBundleIds?: string[] } = {}
): Promise<void> {
  const [active, serviceContract, runtimeLease] = await Promise.all([
    readDaemonActiveBundleState(),
    readDaemonServiceContract().catch(() => null),
    readDaemonRuntimeLease().catch(() => null),
  ]);
  const retain = new Set([
    ...(active ? [active.bundleId] : []),
    ...(serviceContract?.bundleId ? [serviceContract.bundleId] : []),
    ...(runtimeLease?.bundleId ? [runtimeLease.bundleId] : []),
    ...(input.retainBundleIds ?? []),
  ]);
  const bundleEntries = await readDirectoryNames(daemonRuntimeBundlesPath());
  await Promise.all(
    bundleEntries
      .filter((bundleId) => !retain.has(bundleId))
      .map(async (bundleId) => {
        await rm(resolveDaemonBundleRootPath(bundleId), {
          recursive: true,
          force: true,
        });
      })
  );
}

async function resolveDaemonBundleSourceProvenance(
  explicitPath?: string
): Promise<{
  sourceEntryScriptPath: string;
  sourceDistRootPath: string;
  sourcePackageRootPath: string;
  sourceKind: DaemonBundleSourceKind;
}> {
  const sourceEntryScriptPath = await resolveCliScriptPath(explicitPath);
  const sourceDistRootPath = dirname(sourceEntryScriptPath);
  const sourcePackageRootPath = dirname(sourceDistRootPath);
  return {
    sourceEntryScriptPath,
    sourceDistRootPath,
    sourcePackageRootPath,
    sourceKind: inferDaemonBundleSourceKind(sourcePackageRootPath),
  };
}

export async function resolveCurrentDaemonSourceDistFingerprint(
  input: { cliScriptPath?: string } = {}
): Promise<string | null> {
  const sourceProvenance = await resolveDaemonBundleSourceProvenance(
    input.cliScriptPath
  ).catch(() => null);
  if (!sourceProvenance) {
    return null;
  }
  return await computeDaemonSourceDistFingerprint(
    sourceProvenance.sourceDistRootPath
  ).catch(() => null);
}

async function resolveCliScriptPath(explicitPath?: string): Promise<string> {
  const rawPath = explicitPath ?? process.argv[1];
  if (!rawPath) {
    throw new Error(
      "Cannot determine CLI script path for WakeLoop background service bundle."
    );
  }
  const resolvedPath = await realpath(rawPath).catch(async () => {
    const fallbackPath = await resolvePreferredCliScriptPath(rawPath);
    if (fallbackPath) {
      return fallbackPath;
    }
    throw new Error(
      `WakeLoop could not resolve a CLI dist entry for service bundling from ${rawPath}.`
    );
  });
  const preferredPath = await resolvePreferredCliScriptPath(resolvedPath);
  if (preferredPath) {
    return preferredPath;
  }
  throw new Error(
    `WakeLoop could not find a supported CLI dist entry for service bundling from ${resolvedPath}.`
  );
}

function createBundleId(version: string): string {
  const normalizedVersion = version.replace(/[^a-zA-Z0-9._-]+/g, "_");
  return `${normalizedVersion}-${Date.now()}`;
}

async function computeDaemonSourceDistFingerprint(
  sourceDistRootPath: string
): Promise<string> {
  const filePaths = await listDaemonSourceDistFiles(sourceDistRootPath);
  const hash = createHash("sha256");
  for (const filePath of filePaths) {
    const relativePath = relative(sourceDistRootPath, filePath).replaceAll(
      "\\",
      "/"
    );
    hash.update(relativePath, "utf8");
    hash.update("\0", "utf8");
    hash.update(await readFile(filePath));
    hash.update("\0", "utf8");
  }
  return hash.digest("hex");
}

async function listDaemonSourceDistFiles(path: string): Promise<string[]> {
  return await listIncludedDaemonSourceDistFiles(path, path);
}

async function listIncludedDaemonSourceDistFiles(
  sourceDistRootPath: string,
  currentPath: string
): Promise<string[]> {
  const entries = await readdir(currentPath, { withFileTypes: true });
  const filePaths: string[] = [];

  for (const entry of entries) {
    const entryPath = join(currentPath, entry.name);
    if (!isDaemonSourceDistPathIncluded(sourceDistRootPath, entryPath)) {
      continue;
    }
    if (entry.isDirectory()) {
      filePaths.push(
        ...(await listIncludedDaemonSourceDistFiles(
          sourceDistRootPath,
          entryPath
        ))
      );
      continue;
    }
    if (entry.isFile()) {
      filePaths.push(entryPath);
    }
  }

  return filePaths.sort((left, right) => {
    if (left === right) {
      return 0;
    }
    return left < right ? -1 : 1;
  });
}

function isDaemonSourceDistPathIncluded(
  sourceDistRootPath: string,
  candidatePath: string
): boolean {
  const relativePath = relative(sourceDistRootPath, candidatePath).replaceAll(
    "\\",
    "/"
  );
  if (!relativePath || relativePath.startsWith("..")) {
    return true;
  }
  const topLevelPathSegment = relativePath.split("/")[0] ?? "";
  return !DAEMON_SOURCE_DIST_IGNORED_TOP_LEVEL_DIRS.has(topLevelPathSegment);
}

async function collectDaemonBundleRuntimeDependencyClosure(input: {
  sourcePackageRootPath: string;
}): Promise<Array<{ dependencyName: string; packageRootPath: string }>> {
  const packageManifestPath = join(input.sourcePackageRootPath, "package.json");
  const packageManifestRaw = await readFile(packageManifestPath, "utf8").catch(
    (error: unknown) => {
      if (getErrorCode(error) === "ENOENT") {
        throw new Error(
          `WakeLoop could not find the CLI package manifest for background service bundling at ${packageManifestPath}.`
        );
      }
      throw error;
    }
  );
  const packageManifest = JSON.parse(packageManifestRaw) as {
    dependencies?: Record<string, string>;
    optionalDependencies?: Record<string, string>;
  };
  const resolvedDependencies = new Map<string, string>();
  const queue = [
    ...Object.keys(packageManifest.dependencies ?? {}).map(
      (dependencyName) => ({
        dependencyName,
        required: true,
        resolverPackageRootPath: input.sourcePackageRootPath,
      })
    ),
    ...Object.keys(packageManifest.optionalDependencies ?? {}).map(
      (dependencyName) => ({
        dependencyName,
        required: false,
        resolverPackageRootPath: input.sourcePackageRootPath,
      })
    ),
  ];

  while (queue.length > 0) {
    const nextDependency = queue.shift();
    if (!nextDependency) {
      continue;
    }
    if (isTypeOnlyDependencyName(nextDependency.dependencyName)) {
      continue;
    }
    if (resolvedDependencies.has(nextDependency.dependencyName)) {
      continue;
    }
    const dependencyManifest = await readDaemonBundleDependencyManifest({
      dependencyName: nextDependency.dependencyName,
      required: nextDependency.required,
      resolverPackageRootPath: nextDependency.resolverPackageRootPath,
    });
    if (!dependencyManifest) {
      continue;
    }

    resolvedDependencies.set(
      nextDependency.dependencyName,
      dependencyManifest.packageRootPath
    );
    const nestedRequiredDependencies = Object.keys(
      dependencyManifest.dependencies ?? {}
    );
    const nestedOptionalDependencies = Object.keys(
      dependencyManifest.optionalDependencies ?? {}
    );
    const nestedPeerDependencies = Object.keys(
      dependencyManifest.peerDependencies ?? {}
    );
    for (const dependencyName of nestedRequiredDependencies) {
      queue.push({
        dependencyName,
        required: true,
        resolverPackageRootPath: dependencyManifest.packageRootPath,
      });
    }
    for (const dependencyName of nestedOptionalDependencies) {
      queue.push({
        dependencyName,
        required: false,
        resolverPackageRootPath: dependencyManifest.packageRootPath,
      });
    }
    for (const dependencyName of nestedPeerDependencies) {
      queue.push({
        dependencyName,
        required: false,
        resolverPackageRootPath: dependencyManifest.packageRootPath,
      });
    }
  }

  return [...resolvedDependencies.entries()]
    .map(([dependencyName, packageRootPath]) => ({
      dependencyName,
      packageRootPath,
    }))
    .sort((left, right) =>
      left.dependencyName.localeCompare(right.dependencyName)
    );
}

function isTypeOnlyDependencyName(dependencyName: string): boolean {
  return dependencyName.startsWith("@types/");
}

async function readDaemonBundleDependencyManifest(input: {
  dependencyName: string;
  required: boolean;
  resolverPackageRootPath: string;
}): Promise<{
  packageRootPath: string;
  dependencies?: Record<string, string>;
  optionalDependencies?: Record<string, string>;
  peerDependencies?: Record<string, string>;
} | null> {
  const dependencyPackageRootPath = await resolveDaemonDependencyPackageRoot({
    dependencyName: input.dependencyName,
    resolverPackageRootPath: input.resolverPackageRootPath,
    required: input.required,
  });
  if (!dependencyPackageRootPath) {
    return null;
  }
  const dependencyManifestPath = join(
    dependencyPackageRootPath,
    "package.json"
  );
  const dependencyManifestRaw = await readFile(
    dependencyManifestPath,
    "utf8"
  ).catch((error: unknown) => {
    if (getErrorCode(error) === "ENOENT") {
      if (!input.required) {
        return null;
      }
      throw new Error(
        `WakeLoop could not find runtime dependency ${input.dependencyName} for background service bundling at ${dependencyManifestPath}. Run \`pnpm install\` and retry.`
      );
    }
    throw error;
  });
  if (dependencyManifestRaw === null) {
    return null;
  }
  const dependencyManifest = JSON.parse(dependencyManifestRaw) as {
    dependencies?: Record<string, string>;
    optionalDependencies?: Record<string, string>;
    peerDependencies?: Record<string, string>;
  };
  return {
    packageRootPath: dependencyPackageRootPath,
    ...dependencyManifest,
  };
}

async function resolveDaemonDependencyPackageRoot(input: {
  dependencyName: string;
  resolverPackageRootPath: string;
  required: boolean;
}): Promise<string | null> {
  const resolver = createRequire(
    join(input.resolverPackageRootPath, "package.json")
  );
  try {
    const dependencyEntryPath = await realpath(
      resolver.resolve(input.dependencyName)
    );
    return await findDaemonDependencyPackageRoot(dependencyEntryPath);
  } catch (error) {
    if (isPackageEntryResolutionError(error)) {
      const dependencyPackageRootPath =
        await findDaemonDependencyPackageRootFromResolutionPaths({
          dependencyName: input.dependencyName,
          resolver,
        });
      if (dependencyPackageRootPath) {
        return dependencyPackageRootPath;
      }
      if (!input.required) {
        return null;
      }
      throw new Error(
        `WakeLoop could not find runtime dependency ${input.dependencyName} for background service bundling from ${input.resolverPackageRootPath}. Run \`pnpm install\` and retry.`
      );
    }
    throw error;
  }
}

async function findDaemonDependencyPackageRootFromResolutionPaths(input: {
  dependencyName: string;
  resolver: NodeJS.Require;
}): Promise<string | null> {
  const resolutionPaths = input.resolver.resolve.paths(input.dependencyName);
  if (!resolutionPaths) {
    return null;
  }
  for (const nodeModulesPath of resolutionPaths) {
    const candidatePackageRootPath = join(
      nodeModulesPath,
      ...input.dependencyName.split("/")
    );
    if (await pathExists(join(candidatePackageRootPath, "package.json"))) {
      return await realpath(candidatePackageRootPath);
    }
  }
  return null;
}

async function findDaemonDependencyPackageRoot(
  resolvedEntryPath: string
): Promise<string> {
  let currentPath = dirname(resolvedEntryPath);
  while (true) {
    const packageManifestPath = join(currentPath, "package.json");
    if (await pathExists(packageManifestPath)) {
      return currentPath;
    }
    const parentPath = dirname(currentPath);
    if (parentPath === currentPath) {
      break;
    }
    currentPath = parentPath;
  }
  throw new Error(
    `WakeLoop could not determine the package root for background service dependency entry ${resolvedEntryPath}.`
  );
}

function buildDaemonServiceLauncherSource(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  const activeBundleStatePath = JSON.stringify(
    daemonServiceActiveBundlePath(pathInput)
  );
  const installStatePath = JSON.stringify(daemonStatePath(pathInput));
  const bundleRootPath = JSON.stringify(daemonRuntimeBundlesPath(pathInput));
  const allowWorkspaceBundleSource = JSON.stringify(
    resolveWakeLoopEnvPreset(pathInput) === "dev-local"
  );
  return `import { access, readFile, readdir, stat, writeFile } from "node:fs/promises";
import { join } from "node:path";
import process from "node:process";
import { spawn } from "node:child_process";

const ACTIVE_BUNDLE_PATH = ${activeBundleStatePath};
const INSTALL_STATE_PATH = ${installStatePath};
const BUNDLE_ROOT_PATH = ${bundleRootPath};
const ALLOW_WORKSPACE_BUNDLE_SOURCE = ${allowWorkspaceBundleSource};

const ACTIVE_BUNDLE_SCHEMA = "wakeloop-daemon-active-bundle-v2";
const ACTIVE_BUNDLE_LEGACY_SCHEMA = "wakeloop-daemon-active-bundle-v1";
const INSTALL_STATE_SCHEMA = "wakeloop-daemon-install-v1";

async function readJsonFile(path) {
  try {
    return JSON.parse(await readFile(path, "utf8"));
  } catch (error) {
    if (error && typeof error === "object" && "code" in error && error.code === "ENOENT") {
      return null;
    }
    throw error;
  }
}

async function pathExists(path) {
  try {
    await access(path);
    return true;
  } catch (error) {
    if (error && typeof error === "object" && "code" in error && error.code === "ENOENT") {
      return false;
    }
    throw error;
  }
}

function normalizeInstallState(parsed) {
  if (!parsed || parsed.schema !== INSTALL_STATE_SCHEMA || typeof parsed.daemonVersion !== "string" || parsed.daemonVersion.length === 0) {
    return null;
  }
  return {
    daemonVersion: parsed.daemonVersion,
  };
}

function normalizeBundleState(parsed) {
  if (
    parsed &&
    parsed.v === 2 &&
    parsed.schema === ACTIVE_BUNDLE_SCHEMA &&
    typeof parsed.bundleId === "string" &&
    parsed.bundleId.length > 0 &&
    typeof parsed.daemonVersion === "string" &&
    parsed.daemonVersion.length > 0 &&
    typeof parsed.entryScriptRelativePath === "string" &&
    parsed.entryScriptRelativePath.length > 0 &&
    (parsed.bundleFormat === "self_contained_v1" || parsed.bundleFormat === "legacy_linked_v1")
  ) {
    return parsed;
  }
  if (
    parsed &&
    parsed.v === 1 &&
    parsed.schema === ACTIVE_BUNDLE_LEGACY_SCHEMA &&
    typeof parsed.bundleId === "string" &&
    parsed.bundleId.length > 0 &&
    typeof parsed.daemonVersion === "string" &&
    parsed.daemonVersion.length > 0 &&
    typeof parsed.entryScriptRelativePath === "string" &&
    parsed.entryScriptRelativePath.length > 0
  ) {
    return {
      ...parsed,
      bundleFormat: "legacy_linked_v1",
    };
  }
  return null;
}

function normalizeBundlePackageManifest(parsed) {
  if (!parsed || typeof parsed.version !== "string" || parsed.version.length === 0) {
    return null;
  }
  return {
    version: parsed.version,
  };
}

function uniqueNonEmptyStrings(values) {
  return [...new Set(values.filter((value) => typeof value === "string" && value.length > 0))];
}

async function findFallbackSelfContainedBundle(input) {
  const directories = await readdir(BUNDLE_ROOT_PATH, { withFileTypes: true }).catch((error) => {
    if (error && typeof error === "object" && "code" in error && error.code === "ENOENT") {
      return [];
    }
    throw error;
  });
  const entryScriptRelativePaths = uniqueNonEmptyStrings([
    input.preferredEntryScriptRelativePath,
    "dist/wakeloop.js",
    "dist/wakeloop-dev.js",
  ]);
  const candidates = [];
  for (const directory of directories) {
    if (!directory.isDirectory()) {
      continue;
    }
    const bundleId = directory.name;
    const bundlePath = join(BUNDLE_ROOT_PATH, bundleId);
    const manifest = normalizeBundlePackageManifest(
      await readJsonFile(join(bundlePath, "package.json"))
    );
    if (manifest?.version !== input.installState.daemonVersion) {
      continue;
    }
    for (const entryScriptRelativePath of entryScriptRelativePaths) {
      const entryPath = join(bundlePath, entryScriptRelativePath);
      if (!(await pathExists(entryPath))) {
        continue;
      }
      const entryStat = await stat(entryPath).catch(() => null);
      candidates.push({
        bundleId,
        entryPath,
        entryScriptRelativePath,
        mtimeMs: entryStat?.mtimeMs ?? 0,
      });
      break;
    }
  }
  candidates.sort((a, b) => {
    if (b.mtimeMs !== a.mtimeMs) {
      return b.mtimeMs - a.mtimeMs;
    }
    return b.bundleId.localeCompare(a.bundleId);
  });
  return candidates[0] ?? null;
}

async function persistFallbackActiveBundleState(input) {
  const now = new Date().toISOString();
  const bundleRootPath = join(BUNDLE_ROOT_PATH, input.bundleId);
  const nextState = {
    v: 2,
    schema: ACTIVE_BUNDLE_SCHEMA,
    bundleId: input.bundleId,
    daemonVersion: input.installState.daemonVersion,
    entryScriptRelativePath: input.entryScriptRelativePath,
    bundleFormat: "self_contained_v1",
    sourceEntryScriptPath: input.entryPath,
    sourcePackageRootPath: bundleRootPath,
    sourceDistRootPath: join(bundleRootPath, "dist"),
    sourceDistFingerprint: null,
    sourceKind: "package_install",
    createdAt: input.previousBundleState?.createdAt ?? now,
    updatedAt: now,
  };
  await writeFile(ACTIVE_BUNDLE_PATH, \`\${JSON.stringify(nextState, null, 2)}\\n\`, {
    encoding: "utf8",
    mode: 0o600,
  }).catch((error) => {
    console.error(\`WakeLoop Service launcher recovered a matching bundle but could not update active bundle state: \${String(error)}\`);
  });
}

async function resolveEntryPath() {
  const installState = normalizeInstallState(await readJsonFile(INSTALL_STATE_PATH));
  const bundleState = normalizeBundleState(await readJsonFile(ACTIVE_BUNDLE_PATH));
  if (!installState) {
    throw new Error("WakeLoop Service launcher could not read a valid install state.");
  }
  if (!bundleState) {
    const fallback = await findFallbackSelfContainedBundle({
      installState,
      preferredEntryScriptRelativePath: null,
    });
    if (!fallback) {
      throw new Error("WakeLoop Service launcher could not read a valid active bundle state.");
    }
    await persistFallbackActiveBundleState({
      ...fallback,
      installState,
      previousBundleState: null,
    });
    console.error(\`WakeLoop Service launcher recovered bundle \${fallback.bundleId} because active bundle state was missing or invalid.\`);
    return fallback.entryPath;
  }
  if (bundleState.bundleFormat !== "self_contained_v1") {
    const fallback = await findFallbackSelfContainedBundle({
      installState,
      preferredEntryScriptRelativePath: bundleState.entryScriptRelativePath,
    });
    if (!fallback) {
      throw new Error(\`WakeLoop Service launcher refused to start legacy bundle format \${String(bundleState.bundleFormat)}.\`);
    }
    await persistFallbackActiveBundleState({
      ...fallback,
      installState,
      previousBundleState: bundleState,
    });
    console.error(\`WakeLoop Service launcher recovered bundle \${fallback.bundleId} because active bundle state used legacy bundle format \${String(bundleState.bundleFormat)}.\`);
    return fallback.entryPath;
  }
  if (bundleState.sourceKind === "workspace" && !ALLOW_WORKSPACE_BUNDLE_SOURCE) {
    const fallback = await findFallbackSelfContainedBundle({
      installState,
      preferredEntryScriptRelativePath: bundleState.entryScriptRelativePath,
    });
    if (!fallback) {
      throw new Error("WakeLoop Service launcher refused to start repo-local workspace service files in this lane. Repo-local service files are only allowed through \`wakeloop-dev\`.");
    }
    await persistFallbackActiveBundleState({
      ...fallback,
      installState,
      previousBundleState: bundleState,
    });
    console.error(\`WakeLoop Service launcher recovered bundle \${fallback.bundleId} because active bundle state pointed at a repo-local workspace bundle in this lane.\`);
    return fallback.entryPath;
  }
  if (bundleState.daemonVersion !== installState.daemonVersion) {
    const fallback = await findFallbackSelfContainedBundle({
      installState,
      preferredEntryScriptRelativePath: bundleState.entryScriptRelativePath,
    });
    if (!fallback) {
      throw new Error(\`WakeLoop Service launcher detected bundle version drift: bundle v\${String(bundleState.daemonVersion)} != install v\${String(installState.daemonVersion)}.\`);
    }
    await persistFallbackActiveBundleState({
      ...fallback,
      installState,
      previousBundleState: bundleState,
    });
    console.error(\`WakeLoop Service launcher recovered bundle \${fallback.bundleId} because active bundle state had version drift.\`);
    return fallback.entryPath;
  }
  const entryPath = join(BUNDLE_ROOT_PATH, bundleState.bundleId, bundleState.entryScriptRelativePath);
  if (await pathExists(entryPath)) {
    return entryPath;
  }
  const fallback = await findFallbackSelfContainedBundle({
    installState,
    preferredEntryScriptRelativePath: bundleState.entryScriptRelativePath,
  });
  if (!fallback) {
    throw new Error(\`WakeLoop Service launcher could not find active bundle entry \${entryPath}.\`);
  }
  await persistFallbackActiveBundleState({
    ...fallback,
    installState,
    previousBundleState: bundleState,
  });
  console.error(\`WakeLoop Service launcher recovered bundle \${fallback.bundleId} because active bundle entry was missing.\`);
  return fallback.entryPath;
}

async function main() {
  const entryPath = await resolveEntryPath();
  const child = spawn(process.execPath, [entryPath, ...process.argv.slice(2)], {
    stdio: "inherit",
    env: process.env,
    windowsHide: true,
  });

  const forwardSignal = (signal) => {
    if (!child.killed) {
      child.kill(signal);
    }
  };

  for (const signal of ["SIGINT", "SIGTERM"]) {
    process.on(signal, () => {
      forwardSignal(signal);
    });
  }

  child.once("error", (error) => {
    console.error(String(error));
    process.exitCode = 1;
  });

  child.once("close", (code, signal) => {
    if (typeof code === "number") {
      process.exitCode = code;
      return;
    }
    if (signal) {
      process.kill(process.pid, signal);
      return;
    }
    process.exitCode = 1;
  });
}

await main();`;
}

function buildDaemonServiceExecutableWrapperSource(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return [
    "#!/bin/sh",
    `exec ${quoteShellSingle(process.execPath)} ${quoteShellSingle(
      daemonServiceLauncherPath(pathInput)
    )} "$@"`,
  ].join("\n");
}

function quoteShellSingle(value: string): string {
  return `'${value.replaceAll("'", "'\"'\"'")}'`;
}

async function readDirectoryNames(path: string): Promise<string[]> {
  const entries = await readdir(path, { withFileTypes: true }).catch(
    (error) => {
      if (getErrorCode(error) === "ENOENT") {
        return [];
      }
      throw error;
    }
  );
  return entries
    .filter((entry) => entry.isDirectory())
    .map((entry) => entry.name);
}

function inferDaemonBundleSourceKind(
  sourcePackageRootPath: string
): DaemonBundleSourceKind {
  const normalized = sourcePackageRootPath.replaceAll("\\", "/").toLowerCase();
  if (normalized.includes("/.npm/") && normalized.includes("_npx")) {
    return DAEMON_BUNDLE_SOURCE_KIND_NPX_CACHE;
  }
  if (normalized.includes("/node_modules/")) {
    return DAEMON_BUNDLE_SOURCE_KIND_PACKAGE_INSTALL;
  }
  if (normalized.includes("/wakeloop/")) {
    return DAEMON_BUNDLE_SOURCE_KIND_WORKSPACE;
  }
  return DAEMON_BUNDLE_SOURCE_KIND_UNKNOWN;
}

function isDaemonBundleFormat(value: unknown): value is DaemonBundleFormat {
  return (
    value === "legacy_linked_v1" ||
    value === "self_contained_v1" ||
    value === "binary_v1"
  );
}

function isDaemonBundleSourceKind(
  value: unknown
): value is DaemonBundleSourceKind {
  return (
    value === DAEMON_BUNDLE_SOURCE_KIND_WORKSPACE ||
    value === DAEMON_BUNDLE_SOURCE_KIND_PACKAGE_INSTALL ||
    value === DAEMON_BUNDLE_SOURCE_KIND_NPX_CACHE ||
    value === DAEMON_BUNDLE_SOURCE_KIND_UNKNOWN
  );
}

function isPackageEntryResolutionError(error: unknown): boolean {
  const code = getErrorCode(error);
  return (
    code === "MODULE_NOT_FOUND" || code === "ERR_PACKAGE_PATH_NOT_EXPORTED"
  );
}

function normalizeOptionalText(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

async function pathExists(path: string): Promise<boolean> {
  try {
    await stat(path);
    return true;
  } catch (error) {
    if (getErrorCode(error) === "ENOENT") {
      return false;
    }
    throw error;
  }
}

async function resolvePreferredCliScriptPath(
  rawPath: string
): Promise<string | null> {
  const preferredScriptNames = buildPreferredCliScriptNames(rawPath);
  const candidateRoots = [
    dirname(rawPath),
    join(process.cwd(), "dist"),
    join(process.cwd(), "clients", "wakeloop-cli", "dist"),
    dirname(fileURLToPath(import.meta.url)),
    join(dirname(fileURLToPath(import.meta.url)), "..", "dist"),
  ];
  for (const scriptName of preferredScriptNames) {
    for (const candidateRoot of candidateRoots) {
      const candidatePath = join(candidateRoot, scriptName);
      if (await pathExists(candidatePath)) {
        return candidatePath;
      }
    }
  }
  return null;
}

function buildPreferredCliScriptNames(rawPath: string): string[] {
  const requestedScriptName = basename(rawPath).endsWith(".js")
    ? basename(rawPath)
    : `${basename(rawPath)}.js`;
  if (isSupportedDaemonCliEntryName(requestedScriptName)) {
    return [requestedScriptName];
  }
  if (String(process.env.WAKELOOP_DEV_MODE ?? "").trim() === "1") {
    return [...DAEMON_CLI_ENTRY_NAMES];
  }
  return [...DAEMON_CLI_ENTRY_NAMES].reverse();
}

function isSupportedDaemonCliEntryName(
  value: string
): value is (typeof DAEMON_CLI_ENTRY_NAMES)[number] {
  return DAEMON_CLI_ENTRY_NAMES.includes(
    value as (typeof DAEMON_CLI_ENTRY_NAMES)[number]
  );
}

function getErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}
